import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { errorText, useT } from "./i18n";

type Handler = () => void | Promise<unknown>;
type RefreshState = { register: (handler: Handler) => () => void; run: () => Promise<void>; busy: boolean; error: string | null };
const RefreshContext = createContext<RefreshState | null>(null);

/** Refresh data in mounted screens, without remounting forms or replacing their unsaved state. */
export function RefreshProvider({ children }: { children: ReactNode }) {
  const handlers = useRef(new Set<Handler>());
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const register = useCallback((handler: Handler) => { handlers.current.add(handler); return () => { handlers.current.delete(handler); }; }, []);
  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true; setBusy(true); setError(null);
    try {
      const results = await Promise.allSettled(Array.from(handlers.current, (handler) => Promise.resolve().then(handler)));
      const failure = results.find((result) => result.status === "rejected");
      if (failure?.status === "rejected") setError(errorText(failure.reason));
    } finally { running.current = false; setBusy(false); }
  }, []);
  return <RefreshContext.Provider value={{ register, run, busy, error }}>{children}</RefreshContext.Provider>;
}

export function useRefreshHandler(handler: Handler) {
  const context = useContext(RefreshContext);
  const latest = useRef(handler);
  latest.current = handler;
  const register = context?.register;
  useEffect(() => register?.(() => latest.current()), [register]);
}

export function RefreshButton({ reloadPage = false }: { reloadPage?: boolean }) {
  const t = useT();
  const context = useContext(RefreshContext);
  if (!context) return null;
  return <span className="refresh-control">
    <button type="button" className="refresh-button" disabled={context.busy} aria-busy={context.busy} onClick={() => { if (reloadPage) location.reload(); else void context.run(); }} title={t.design.refresh}>
      <Icon name="refresh" size={16} />{context.busy ? t.design.refreshing : t.design.refresh}
    </button>
    {context.error && <span className="refresh-error small error" role="alert">{context.error}</span>}
  </span>;
}
